import { defaultSchema } from 'rehype-sanitize';
import type { Options as SanitizeSchema } from 'rehype-sanitize';

// Agent replies are Markdown with raw HTML allowed (rehype-raw), and an agent's text can be steered by what it
// reads (prompt injection). An allowlist keeps that HTML from running code, sending data out or covering the page:
// GitHub's sanitize schema, narrowed to what a reply needs. No svg or math elements (charts and images come as file
// attachments), no style, no srcset, no class but language-* / math-* on code; ids and names get the user-content-
// prefix. safeUrl then keeps a URL the browser loads by itself only on the app's own origin.
// Use: remarkRehypeOptions={{ clobberPrefix: '' }} (the sanitizer prefixes footnote ids, once), rehypePlugins
// [rehypeRaw, [rehypeSanitize, SANITIZE_SCHEMA], rehypeKatex] (KaTeX's output is trusted) and urlTransform={safeUrl}.

export const SANITIZE_SCHEMA: SanitizeSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames ?? []).filter((name) => name !== 'picture' && name !== 'source'), 'checkicon'],
  attributes: {
    ...defaultSchema.attributes,
    code: [['className', /^language-./, 'math-inline', 'math-display']],
  },
  protocols: { ...defaultSchema.protocols, href: ['http', 'https', 'mailto'] },
  strip: ['script', 'style', 'template', 'noscript', 'iframe', 'object', 'embed', 'svg', 'math', 'textarea', 'title'],
};

const LINK_PROTOCOLS = new Set(['http:', 'https:', 'mailto:']);

/**
 * react-markdown's urlTransform: a link (href, shown as a button) keeps http(s) and mailto; any other URL attribute
 * (src, poster, ...) is kept only when it resolves to the app's own origin, the way the browser resolves it
 * (backslashes, scheme-relative and encoded forms included). Anything else becomes ''.
 */
export const safeUrl = (url: string, key: string): string => {
  let resolved: URL;
  try {
    resolved = new URL(url, window.location.origin);
  } catch {
    return '';
  }
  if (key === 'href') return LINK_PROTOCOLS.has(resolved.protocol) ? url : '';
  return resolved.origin === window.location.origin ? url : '';
};
